import { useAppDispatch, useAppSelector } from 'store/hooks';
import React from 'react';
import { MainLayout } from 'layouts/MainLayout';
import { Box, Tabs } from '@mui/material';
import { MARKETING_3RD_PARTY_PERKS, MARKETING_PEPPER_PERKS, MARKETING_PERK_TEMPLATES } from 'config/routes';
import { TabContext } from '@mui/lab';
import { PepperPerksView } from './PepperPerksView';
import { LinkTab } from 'ui/LinkTab';
import { useParams } from 'react-router-dom';
import { ExternalPerksView } from './ExternalPerksView';
import { PerkTemplatesView } from './PerkTemplatesView';
import { contentApi } from 'components/content/contentApi';
import { ContentLayout } from 'components/content/models/ContentLayout';
import { setCurrentLayoutSuccess } from 'store/content/contentSlice';
import { getAllLocationTitles } from 'store/locations/locationsSlice';
import { loadAllTemplates } from 'store/perk-templates/perkTemplatesSlice';
import { getAllPerks } from 'store/perks/perksSlice';
import { Row } from 'ui/Flex';
import { MuiChip } from 'ui/MuiChip';

const PerksPage = () => {
    const dispatch = useAppDispatch();
    const { type } = useParams<{ type: 'pepper' | 'external' | 'templates' | undefined }>();
    const { settings } = useAppSelector(state => state.settings);

    const globalLoyaltyProvider = React.useMemo(() => settings?.loyaltyProvider, [settings?.loyaltyProvider]);
    React.useEffect(() => {
        getAllLocationTitles(dispatch);
        dispatch(loadAllTemplates());
        dispatch(getAllPerks());
    }, [dispatch]);

    React.useEffect(() => {
        async function load() {
            const activeLayoutResult = await contentApi.layouts.getActiveLayout();
            if (activeLayoutResult.ok) {
                const activeLayout = activeLayoutResult.body as ContentLayout;
                dispatch(setCurrentLayoutSuccess(activeLayout));
            }
        }
        load();
    }, [dispatch]);

    const [value, setValue] = React.useState(() => {
        if (type === 'external') {
            return MARKETING_3RD_PARTY_PERKS;
        }
        if (type === 'templates') {
            return MARKETING_PERK_TEMPLATES;
        }

        return MARKETING_PEPPER_PERKS;
    });

    const handleChange = React.useCallback((_: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
    }, []);
    const hasExternalPerkSetting = React.useMemo(
        () =>
            !!globalLoyaltyProvider &&
            globalLoyaltyProvider !== 'PEPPER' &&
            globalLoyaltyProvider !== 'COMTREX',
        [globalLoyaltyProvider]
    );

    const templatesTab = React.useCallback(
        () => (
            <Row valign="center">
                Templates
                <MuiChip
                    label="new"
                    color="purple"
                    sx={{
                        ml: 1,
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        height: '20px'
                    }}
                />
            </Row>
        ),
        []
    );

    return (
        <MainLayout
            pageName="Perks"
            pageDescription="Incentivize your customers with loyalty schemes."
            noScroll
        >
            <TabContext value={value}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs variant="fullWidth" value={value} indicatorColor="primary" onChange={handleChange}>
                        <LinkTab
                            value={MARKETING_PEPPER_PERKS}
                            label="Pepper"
                            href={MARKETING_PEPPER_PERKS}
                        />
                        {hasExternalPerkSetting && (
                            <LinkTab
                                value={MARKETING_3RD_PARTY_PERKS}
                                label={`${globalLoyaltyProvider}`}
                                href={MARKETING_3RD_PARTY_PERKS}
                            />
                        )}
                        <LinkTab
                            value={MARKETING_PERK_TEMPLATES}
                            label={templatesTab()}
                            href={MARKETING_PERK_TEMPLATES}
                        />
                    </Tabs>
                </Box>
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', pb: 6 }}>
                    {value === MARKETING_PEPPER_PERKS && <PepperPerksView />}
                    {!!globalLoyaltyProvider &&
                        globalLoyaltyProvider !== 'PEPPER' &&
                        value === MARKETING_3RD_PARTY_PERKS && (
                            <ExternalPerksView loyaltyProvider={globalLoyaltyProvider} />
                        )}
                    {value === MARKETING_PERK_TEMPLATES && <PerkTemplatesView />}
                </Box>
            </TabContext>
        </MainLayout>
    );
};

export default PerksPage;
